import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";

import {
  selectSession,
  userDetailSelector,
  selectSelectedSchool,
} from "app/modules/schools/selectors";

import { userDocIdSelector } from "app/modules/auth/selectors";
import { getLoggedInUserDetail } from "app/modules/schools/thunk";
import { generateNameInitial } from "app/utils/helper";
import {
  Box,
  Container,
  Stack,
  Typography,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";

import UserProfileDrawer from "./components/UserProfileDrawer";
import { userPrimaryRoleSelector } from "app/modules/schools/selectors";

const UserHeader = ({ handleOnClickHamburgeMenu }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const dispatch = useDispatch();
  const userDocId = useSelector(userDocIdSelector);
  const selectedSchool = useSelector(selectSelectedSchool);
  const userDetail = useSelector(userDetailSelector);
  const primaryRole = useSelector(userPrimaryRoleSelector);
  const sessionlist = useSelector(selectSession);
  const sessions = [];
  const [state, setState] = useState({
    showSidebar: false,
  });

  const createSessionList = () => {
    sessionlist.forEach((session) => {
      sessions.push(session.sessionName);
    });
  };
  createSessionList();

  // Fetch user Details
  useEffect(() => {
    dispatch(getLoggedInUserDetail({ staffDocId: userDocId }));
  }, [userDocId, dispatch]);

  const getWelcomeMessage = () => {
    if (isMobile) return "Ekalsutra School Management System";
    if (isTablet) return "Ekalsutra School Management System";
    return "Streamline Your School Operations with Ekalsutra";
  };

  return (
    <>
      <UserProfileDrawer state={state} setState={setState} />
      <StyledContainer>
        <div className="inner-nav">
          <Container maxWidth="xl">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ height: "100%" }}
            >
              {/* Left Section - School Info */}
              <Stack direction="row" alignItems="center" gap={1.5} flex={1}>
                {isMobile && handleOnClickHamburgeMenu && (
                  <IconButton
                    onClick={handleOnClickHamburgeMenu}
                    sx={{ color: "#0C2F49", padding: "8px" }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}

                {selectedSchool?.instituteType ? (
                  <Link
                    to="/schools"
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <Stack
                      direction="row"
                      gap={1.5}
                      alignItems="center"
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          opacity: 0.85,
                        },
                      }}
                    >
                      <LogoWrapper>
                        <img
                          src={
                            selectedSchool?.schoolLogo ??
                            "/image-placeholder.jpeg"
                          }
                          alt="School Logo"
                          className="school-logo"
                        />
                      </LogoWrapper>
                      {!isMobile && (
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            className="school-name"
                            sx={{
                              fontSize: { xs: "16px", md: "18px", lg: "20px" },
                              fontWeight: 600,
                              color: "#0C2F49",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: {
                                sm: "200px",
                                md: "300px",
                                lg: "400px",
                              },
                            }}
                          >
                            {selectedSchool?.schoolName}
                          </Typography>
                          {!isTablet && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#5A7184",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: { md: "300px", lg: "400px" },
                              }}
                            >
                              {`${selectedSchool?.schoolAddress?.dist}, ${selectedSchool?.schoolAddress?.state} ${selectedSchool?.schoolAddress?.pinCode}`}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Link>
                ) : (
                  <Typography
                    className="welcome-text"
                    sx={{
                      fontSize: { xs: "16px", sm: "18px", md: "20px" },
                      fontWeight: 500,
                      color: "#0C2F49",
                    }}
                  >
                    {getWelcomeMessage()}
                  </Typography>
                )}
              </Stack>

              {/* Right Section - User Info */}
              <Stack
                direction="row"
                alignItems="center"
                gap={{ xs: 1, sm: 1.5, md: 2 }}
              >
                {/* User Profile Info */}
                {!isMobile && (
                  <ProfileNameWrapper>
                    <p className="profile-name">
                      {userDetail?.firstName} {userDetail?.lastName}
                    </p>
                    <p className="role" title={primaryRole}>
                      {primaryRole}
                    </p>
                  </ProfileNameWrapper>
                )}

                {/* Profile Avatar */}
                <Tooltip title="Profile" arrow>
                  <AvatarWrapper
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        showSidebar: !prev.showSidebar,
                      }))
                    }
                  >
                    {userDetail?.profilePic ? (
                      <img
                        src={userDetail?.profilePic}
                        className="profile-avatar"
                        alt="Profile"
                      />
                    ) : (
                      <div className="avatar-initials">
                        {generateNameInitial(
                          userDetail?.firstName,
                          userDetail?.lastName
                        )}
                      </div>
                    )}
                  </AvatarWrapper>
                </Tooltip>
              </Stack>
            </Stack>
          </Container>
        </div>
      </StyledContainer>
    </>
  );
};

export default UserHeader;

// Styled Components
const StyledContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;

  .inner-nav {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    height: 70px;
    display: flex;
    align-items: center;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      height: 64px;
    }
  }
`;

const LogoWrapper = styled.div`
  .school-logo {
    height: 52px;
    width: 52px;
    object-fit: cover;
    border-radius: 12px;
    border: 2px solid rgba(60, 110, 239, 0.2);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(60, 110, 239, 0.2);
    }

    @media (max-width: 768px) {
      height: 44px;
      width: 44px;
      border-radius: 10px;
    }
  }
`;

const ProfileNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  max-width: 180px;

  .profile-name {
    font-size: 15px;
    font-weight: 600;
    color: #0c2f49;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-align: right;
    line-height: 1.2;
    margin: 0;
  }

  .role {
    font-size: 12px;
    font-weight: 400;
    color: #5a7184;
    text-transform: capitalize;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-align: right;
    line-height: 1.2;
    margin: 0;
  }

  @media (max-width: 1024px) {
    max-width: 140px;
  }
`;

const AvatarWrapper = styled.div`
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  .profile-avatar {
    height: 48px;
    width: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #3c6eef;
    box-shadow: 0 2px 8px rgba(60, 110, 239, 0.2);
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      height: 42px;
      width: 42px;
    }
  }

  .avatar-initials {
    height: 48px;
    width: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3c6eef 0%, #5b8def 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 16px;
    box-shadow: 0 2px 8px rgba(60, 110, 239, 0.2);
    border: 2px solid #e8f0fe;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      height: 42px;
      width: 42px;
      font-size: 14px;
    }
  }

  &:hover .profile-avatar,
  &:hover .avatar-initials {
    box-shadow: 0 4px 16px rgba(60, 110, 239, 0.3);
  }
`;
